import React, { useContext, useState, useEffect, useMemo } from "react";
import { useSpeakingDetector } from "../../hooks/useSpeakingDetector";
import { ThemeContext } from "../../contexts/ThemeContext";
import ParticipantAudio from "./ParticipantAudio";
import MuteComponent from "./MuteComponent";
import MeetingActiveSpeaker from "./MeetingActiveSpeaker";
import DegradedBadge from "./DegradedBadge";
import { useVisibility } from "../../hooks/useVisibility";

function GridCell({ participant, isSpeaking, activeSpeakerId, color, degraded }) {
  const [ref, isVisible] = useVisibility();
  const track = participant.stream?.getVideoTracks?.()[0];
  const speaking =
    (participant.isLocal && isSpeaking) ||
    (!participant.isLocal && activeSpeakerId === participant.userId);

  return (
    <div
      ref={ref}
      key={`grid_${participant.userId}`}
      className="video-container-wrtc"
      style={{ position: "relative", overflow: "hidden", borderRadius: "10px" }}
    >
      {!participant.audioOn && <MuteComponent />}
      {degraded && <DegradedBadge />}
      {participant.videoOn && track ? (
        isVisible ? (
          <video
            ref={(videoRef) => {
              if (videoRef && videoRef.srcObject !== participant.stream) {
                videoRef.srcObject = participant.stream;
              }
            }}
            autoPlay
            muted={participant.isLocal}
            playsInline
            style={{
              cursor: "pointer",
              transform: participant.isLocal ? "scaleX(-1)" : "none",
              objectFit: "cover",
              width: "100%",
              height: "100%",
              borderRadius: "10px",
            }}
          />
        ) : (
          <div className="video-off-blur" style={{ height: "100%" }}>
            <img src={participant.userImage} alt="Background" className="blur-bg" />
            <div className={`avatar-wrapper ${speaking ? "speaking" : ""}`}>
              {participant.userImage ? (
                <img src={participant.userImage} alt="User" className="user-avatar user-avatar-grid" />
              ) : (
                <div
                  className="user-avatar user-avatar-grid"
                  style={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    fontSize: "clamp(16px, 3vw, 50px)",
                    color,
                  }}
                >
                  {participant.displayName?.[0]}
                  {participant.displayName?.split(" ")[1]?.charAt(0)}
                </div>
              )}
            </div>
          </div>
        )
      ) : (
        <div className="video-off-blur" style={{ height: "100%" }}>
          <ParticipantAudio participant={participant} />
          <img src={participant.userImage} alt="Background" className="blur-bg" />
          <div className={`avatar-wrapper ${speaking ? "speaking" : ""}`}>
            {participant.userImage ? (
              <img src={participant.userImage} alt="User" className="user-avatar user-avatar-grid" />
            ) : (
              <div
                className="user-avatar user-avatar-grid"
                style={{
                  cursor: "pointer",
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "center",
                  fontSize: "clamp(16px, 3vw, 50px)",
                  color,
                }}
              >
                {participant.displayName?.[0]}
                {participant.displayName?.split(" ")[1]?.charAt(0)}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Audio always mounted even when video is lazy-hidden */}
      {participant.videoOn && track && <ParticipantAudio participant={participant} />}
      <span style={nameOverlayStyle}>{participant.displayName}</span>
    </div>
  );
}

const nameOverlayStyle = {
  position: "absolute",
  bottom: "12px",
  left: "19px",
  zIndex: 2,
  color: "#fff",
  fontSize: "clamp(11px, 1.2vw, 14px)",
  fontWeight: 600,
  backgroundColor: "rgba(0,0,0,0.5)",
  padding: "2px 8px",
  borderRadius: "6px",
  pointerEvents: "none",
  maxWidth: "80%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

export default function MeetingGrid({ users, mediasTools, activeSpeakerId, isMobile }) {
  const { localStream, degradedUserIds } = mediasTools;
  const { theme } = useContext(ThemeContext);
  const [color, setColor] = useState(theme === "dark" ? "#fff" : "#023377");
  const isSpeaking = useSpeakingDetector(localStream, false);
  const [isTablet, setIsTablet] = useState(
    !isMobile && window.innerWidth <= 991
  );

  useEffect(() => {
    setColor(theme === "dark" ? "#fff" : "#023377");
  }, [theme]);

  useEffect(() => {
    const handleResize = () => setIsTablet(!isMobile && window.innerWidth <= 991);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  const { cols, rows } = useMemo(() => {
    if (isTablet) {
      const c = Math.min(2, Math.ceil(Math.sqrt(users.length)));
      const r = Math.ceil(users.length / c);
      return { cols: c, rows: r };
    }
    const c = Math.ceil(Math.sqrt(users.length));
    const r = Math.ceil(users.length / c);
    return { cols: c, rows: r };
  }, [users.length, isTablet]);

  if (isMobile) {
    return (
      <MeetingActiveSpeaker
        users={users}
        mediasTools={mediasTools}
        activeSpeakerId={activeSpeakerId}
      />
    );
  }

  return (
    <div
      className="meeting-grid-container"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: isTablet
          ? `repeat(${rows}, minmax(150px, 1fr))`
          : `repeat(${rows}, 1fr)`,
        gap: "4px",
        height: "calc(100dvh - var(--appbar-height, 73px) - 80px)",
        overflow: isTablet ? "auto" : "hidden",
      }}
    >
      {users.map((participant) => (
        <GridCell
          key={`grid_${participant.userId}`}
          participant={participant}
          isSpeaking={isSpeaking}
          activeSpeakerId={activeSpeakerId}
          color={color}
          degraded={degradedUserIds?.includes(participant.userId)}
        />
      ))}
    </div>
  );
}
